import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';

interface Props {
  strategyId?: string;
  onComplete: () => void;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const ModuleXDecision: React.FC<Props> = ({ strategyId, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Cargar historial de chat si existe
    const loadChat = async () => {
      try {
        const res = await api.get(`/strategies/${strategyId}`);
        const data = res.data.module_x_decision;
        if (data) {
          const dec = typeof data === 'string' ? JSON.parse(data) : data;
          if (dec.activar_diagnostico_inversion) {
            // Ya se tomó una decisión previa
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    loadChat();
  }, [strategyId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleDecision = async (decision: 'Sí' | 'No') => {
    setLoading(true);
    try {
      const res = await api.post(`/strategies/${strategyId}/modules/10/process`, {
        decision: decision
      });
      if (res.status === 200) {
        onComplete();
      }
    } catch (err) {
      console.error(err);
      alert('Error al registrar la decisión');
    } finally {
      setLoading(false);
    }
  };

  const handleSendChat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatMessage.trim() || loading) return;

    const userMsg = chatMessage;
    setChatMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      // Usar endpoint de chat de módulo
      const res = await api.post(`/strategies/${strategyId}/modules/10/chat`, {
        message: userMsg
      });

      // El backend interpretará la respuesta y registrará en la DB si corresponde
      const reply = res.data; // En endpoint de chat, puede devolver texto o JSON
      let replyText = typeof reply === 'string' ? reply : reply.reply || 'Procesado';

      setMessages(prev => [...prev, { role: 'assistant', content: replyText }]);

      // Recargar estrategia para verificar si ya se cerró la decisión
      const stratRes = await api.get(`/strategies/${strategyId}`);
      const data = stratRes.data.module_x_decision;
      if (data) {
        const dec = typeof data === 'string' ? JSON.parse(data) : data;
        if (dec.activar_diagnostico_inversion === 'Sí' || dec.activar_diagnostico_inversion === 'No') {
          // Si el backend ya cerró la decisión, avanzamos
          setTimeout(() => {
            onComplete();
          }, 1500);
        }
      }
    } catch (err) {
      console.error(err);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Hubo un error al procesar tu respuesta. Por favor, intenta de nuevo o pulsa los botones.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Módulo X: Bifurcación Opcional</h2>
      <p style={styles.description}>
        ¿Quieres integrar en la estrategia información sobre la inversión de la categoría?
      </p>

      <div style={styles.alertCard}>
        <h4 style={styles.alertTitle}>💡 Información de Inversión Requerida</h4>
        <p style={styles.alertText}>
          Recuerda que para activar esta rama avanzada (Diagnóstico Competitivo de Inversión y Media Mix), debes tener a la mano la siguiente información:
        </p>
        <ol style={styles.alertList}>
          <li><strong>Media mix de inversión de la categoría</strong> en un periodo determinado (excluyendo a la marca analizada).</li>
          <li><strong>Volumen de inversión por marca competidora</strong> dentro de esa categoría y periodo.</li>
          <li><strong>Presupuesto disponible</strong> de la marca para ese mismo periodo.</li>
        </ol>
        <p style={styles.alertNote}>
          * Es importante que toda la información esté expresada en la misma moneda y corresponda al mismo periodo de análisis.
        </p>
      </div>

      <div style={styles.buttonGroup}>
        <button 
          style={{ ...styles.decisionBtn, ...styles.yesBtn }} 
          onClick={() => handleDecision('Sí')}
          disabled={loading}
        >
          <span style={{ fontSize: '18px', fontWeight: 600 }}>Sí</span>
          <span style={{ fontSize: '12px', opacity: 0.8 }}>Activar rama de diagnóstico competitivo</span>
        </button>
        <button 
          style={{ ...styles.decisionBtn, ...styles.noBtn }} 
          onClick={() => handleDecision('No')}
          disabled={loading}
        >
          <span style={{ fontSize: '18px', fontWeight: 600 }}>No</span>
          <span style={{ fontSize: '12px', opacity: 0.8 }}>Continuar con flujo tradicional (Módulo VI)</span>
        </button>
      </div>

      {/* Chat de Iteración */}
      <div style={styles.chatSection}>
        <h4 style={styles.chatTitle}>O responde escribiendo al asistente</h4>
        <div style={styles.chatContainer}>
          <div style={styles.messageList}>
            {messages.length === 0 && (
              <div style={styles.emptyChat}>
                Escribe cosas como "sí, quiero", "no tengo información", "sigamos normal" o "quiero integrar inversión".
              </div>
            )}
            {messages.map((m, idx) => (
              <div 
                key={idx} 
                style={{
                  ...styles.messageWrapper,
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <div 
                  style={{
                    ...styles.messageBubble,
                    background: m.role === 'user' ? 'var(--accent)' : 'var(--bg-hover)',
                    color: m.role === 'user' ? '#fff' : 'var(--text-primary)',
                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  {m.content}
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form style={styles.chatForm} onSubmit={handleSendChat}>
            <input 
              style={styles.chatInput} 
              value={chatMessage} 
              onChange={e => setChatMessage(e.target.value)}
              placeholder="Escribe tu respuesta aquí..." 
              disabled={loading}
            />
            <button style={styles.sendBtn} type="submit" disabled={loading || !chatMessage.trim()}>
              Enviar
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', flexDirection: 'column', gap: '20px' },
  title: { fontSize: '24px', color: 'var(--text-primary)', fontWeight: 600, margin: 0 },
  description: { fontSize: '16px', color: 'var(--text-secondary)', lineHeight: 1.5 },
  alertCard: {
    background: 'rgba(59, 130, 246, 0.05)',
    border: '1px solid rgba(59, 130, 246, 0.2)',
    borderRadius: '8px',
    padding: '20px',
    color: 'var(--text-primary)'
  },
  alertTitle: { margin: '0 0 10px 0', fontSize: '15px', fontWeight: 600, color: 'var(--accent)' },
  alertText: { fontSize: '14px', margin: '0 0 12px 0', lineHeight: 1.5 },
  alertList: { fontSize: '14px', margin: '0 0 12px 0', paddingLeft: '20px', lineHeight: 1.6 },
  alertNote: { fontSize: '12px', color: 'var(--text-muted)', margin: 0, fontStyle: 'italic' },
  buttonGroup: { display: 'flex', gap: '20px', marginTop: '10px' },
  decisionBtn: {
    flex: 1,
    padding: '20px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease-in-out',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  yesBtn: {
    background: 'var(--success)',
    color: '#fff'
  },
  noBtn: {
    background: 'var(--bg-hover)',
    border: '1px solid var(--border)',
    color: 'var(--text-primary)'
  },
  chatSection: { marginTop: '24px', borderTop: '1px solid var(--border)', paddingTop: '20px' },
  chatTitle: { margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' },
  chatContainer: {
    border: '1px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--bg-card)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '250px'
  },
  messageList: { flex: 1, padding: '16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' },
  emptyChat: { color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', marginTop: '40px', fontStyle: 'italic' },
  messageWrapper: { display: 'flex', width: '100%' },
  messageBubble: { padding: '10px 16px', borderRadius: '16px', maxWidth: '75%', fontSize: '14px', lineHeight: 1.4 },
  chatForm: { display: 'flex', borderTop: '1px solid var(--border)', background: 'var(--bg-card)' },
  chatInput: { flex: 1, padding: '12px 16px', border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' },
  sendBtn: { background: 'var(--accent)', color: '#fff', border: 'none', padding: '0 20px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }
};

export default ModuleXDecision;
